import { Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import Countdown from "react-countdown";
import { FaCheck } from "react-icons/fa";

import { FloatingContainer, Modal } from "../../Wrappers";
import { Title1, Body } from "../../Text";
import { Button } from "../../Button";
import { UserBadge } from "../../UserBadge";
import { useUser } from "MVVM/Hooks/useUser";

interface IProForADayConfirmationProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  expirationDate?: Date;
}

export const ProForADayConfirmation = ({
  isOpen,
  setIsOpen,
}: IProForADayConfirmationProps) => {
  const { getFreshProfile } = useUser();

  const oneDayFromNow = new Date();
  oneDayFromNow.setDate(oneDayFromNow.getDate() + 1);

  const handleClose = () => {
    setIsOpen(false);
    getFreshProfile();
  };

  return (
    <Modal
      isModalOpen={isOpen}
      setModalOpen={setIsOpen}
      handleModalClose={handleClose}
      disableOutsideClose={false}
    >
      <StyledFloatingContainer>
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
        >
          <BadgeWrapper>
            <UserBadge userGroup="PRO" size="xl" />
          </BadgeWrapper>

          <StyledTitle1 mt={4}>Congrats, You've Got Pro!</StyledTitle1>

          <SubHeaderText mt={3} mb={4}>
            <Text as="span">For the next </Text>
            <TimerWrapper>
              <Countdown
                date={oneDayFromNow}
                renderer={(props) => (
                  <InlineTimer>
                    {String(props.days).padStart(2, "0")}:
                    {String(props.hours).padStart(2, "0")}:
                    {String(props.minutes).padStart(2, "0")}:
                    {String(props.seconds).padStart(2, "0")}
                  </InlineTimer>
                )}
              />
            </TimerWrapper>
            <Text as="span">
              , enjoy all the benefits that come with an Allstar Pro
              Subscription.
            </Text>
          </SubHeaderText>

          <BenefitsContainer>
            <BenefitItem>
              <CheckIconWrapper>
                <FaCheck />
              </CheckIconWrapper>
              <BenefitText>Enjoy a completely Ad-Free Experience!</BenefitText>
            </BenefitItem>

            <BenefitItem>
              <CheckIconWrapper>
                <FaCheck />
              </CheckIconWrapper>
              <BenefitText>
                Unlimited Remixes, Downloads and Mobile Clips!
              </BenefitText>
            </BenefitItem>

            <BenefitItem>
              <CheckIconWrapper>
                <FaCheck />
              </CheckIconWrapper>
              <BenefitText>
                Get access to all Premium & Rare Creator Cards!
              </BenefitText>
            </BenefitItem>

            <BenefitItem>
              <CheckIconWrapper>
                <FaCheck />
              </CheckIconWrapper>
              <BenefitText>Early Access to new features!</BenefitText>
            </BenefitItem>
          </BenefitsContainer>

          <ContinueButton size="small" mt={6} onClick={handleClose}>
            Continue
          </ContinueButton>
        </Flex>
      </StyledFloatingContainer>
    </Modal>
  );
};

const StyledFloatingContainer = styled(FloatingContainer)`
  max-width: 600px;
  padding: 40px 32px;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    padding: 32px 24px;
  }
`;

const BadgeWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const StyledTitle1 = styled(Title1)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 32px;
  font-weight: 700;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    font-size: 28px;
  }
`;

const SubHeaderText = styled(Body)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  max-width: 500px;
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    font-size: 14px;
  }
`;

const TimerWrapper = styled(Text).attrs({ as: "span" })`
  display: inline;
`;

const InlineTimer = styled(Text).attrs({ as: "span" })`
  color: ${({ theme }) => theme.colors.envy};
  font-family: "Barlow", sans-serif;
  font-weight: 700;
  white-space: nowrap;
`;

const BenefitsContainer = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  width: 100%;
`;

const BenefitItem = styled(Flex)`
  align-items: flex-start;
  gap: 12px;
  text-align: left;
`;

const CheckIconWrapper = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.ice};
  flex-shrink: 0;
  font-size: 12px;
  height: 24px;
  justify-content: center;
  margin-top: 2px;
  width: 24px;
`;

const BenefitText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    font-size: 14px;
  }
`;

const ContinueButton = styled(Button)`
  width: 100%;
  max-width: 300px;
`;
